const ACCOUNTS_KEY = 'dc-saved-accounts-v1';
const PREFERENCE_KEY = 'dc-account-save-preference-v1';
export const ACCOUNTS_EVENT = 'dc-saved-accounts-changed';
export const SAVE_MODES = ['none', 'password', 'one_click'];

function textField(value) {
    return typeof value === 'string' ? value : '';
}

// Role display metadata; entries saved before roles were stored have role null.
function roleFields(source) {
    const role = typeof source.role === 'string' ? parseInt(source.role, 10) : source.role;
    return {
        role: Number.isInteger(role) ? role : null,
        superAdmin: source.superAdmin === true || source.is_superadmin === true || source.is_superadmin === 1,
    };
}

export function readAccounts() {
    try {
        const parsed = JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || '[]');
        if (!Array.isArray(parsed)) return [];

        const seen = new Set();
        return parsed.filter(account => {
            if (!account || !account.id || typeof account.username !== 'string' || !account.username) return false;
            const id = String(account.id);
            if (seen.has(id)) return false;

            seen.add(id);
            return true;
        }).map(account => ({
            id: String(account.id),
            username: account.username,
            firstName: textField(account.firstName),
            lastName: textField(account.lastName),
            companyName: textField(account.companyName),
            ...roleFields(account),
            mode: account.mode === 'one_click' && /^[0-9a-f]{32}$/.test(account.selector || '') ? 'one_click' : 'password',
            selector: /^[0-9a-f]{32}$/.test(account.selector || '') ? account.selector : null,
            lastUsed: Number.isFinite(account.lastUsed) ? account.lastUsed : 0,
        })).sort((left, right) => right.lastUsed - left.lastUsed).slice(0, 5);
    } catch {
        return [];
    }
}

function writeAccounts(accounts) {
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    window.dispatchEvent(new CustomEvent(ACCOUNTS_EVENT));
}

export function readPreference() {
    try {
        const mode = localStorage.getItem(PREFERENCE_KEY);
        return SAVE_MODES.includes(mode) ? mode : null;
    } catch {
        return null;
    }
}

export function setPreference(mode) {
    if (!SAVE_MODES.includes(mode)) throw new Error('Choose a save preference.');

    localStorage.setItem(PREFERENCE_KEY, mode);
    window.dispatchEvent(new CustomEvent(ACCOUNTS_EVENT));
}

export function saveAccount(user, mode, selector = null, replacementId = null) {
    if (!user?.id || !user.username || !['password', 'one_click'].includes(mode)) {
        throw new Error('A signed-in account is required.');
    }
    if (mode === 'one_click' && !/^[0-9a-f]{32}$/.test(selector || '')) {
        throw new Error('The remembered login could not be saved.');
    }

    const accounts = readAccounts();
    const existing = accounts.some(account => account.id === String(user.id));
    if (!existing && accounts.length >= 5 && !accounts.some(account => account.id === String(replacementId))) {
        throw new Error('Choose a saved account to replace.');
    }

    const next = accounts.filter(account => account.id !== String(user.id) && account.id !== String(replacementId));
    next.unshift({
        id: String(user.id),
        username: user.username,
        firstName: textField(user.firstName),
        lastName: textField(user.lastName),
        companyName: textField(user.companyName),
        ...roleFields(user),
        mode,
        selector: mode === 'one_click' ? selector : null,
        lastUsed: Date.now(),
    });
    writeAccounts(next);
}

export function removeAccount(id) {
    writeAccounts(readAccounts().filter(account => account.id !== String(id)));
}

export function markPasswordRequired(id, expectedSelector) {
    writeAccounts(readAccounts().map(account => account.id === String(id)
        && (expectedSelector === undefined || account.selector === expectedSelector)
        ? { ...account, mode: 'password', selector: null }
        : account));
}

export function markAllPasswordRequired() {
    writeAccounts(readAccounts().map(account => ({ ...account, mode: 'password', selector: null })));
}

/** Marks a successful login and refreshes display metadata (names, role) when the user is given. */
export function touchAccount(id, user) {
    const accounts = readAccounts();
    const account = accounts.find(entry => entry.id === String(id));
    if (!account) return;

    const refreshed = user ? {
        firstName: textField(user.firstName),
        lastName: textField(user.lastName),
        ...(user.companyName ? { companyName: textField(user.companyName) } : {}),
        ...roleFields(user),
    } : {};
    writeAccounts([{ ...account, ...refreshed, lastUsed: Date.now() }, ...accounts.filter(entry => entry !== account)]);
}

export function subscribeAccounts(listener) {
    const onStorage = event => {
        if (!event.key || [ACCOUNTS_KEY, PREFERENCE_KEY].includes(event.key)) listener();
    };
    window.addEventListener(ACCOUNTS_EVENT, listener);
    window.addEventListener('storage', onStorage);
    return () => {
        window.removeEventListener(ACCOUNTS_EVENT, listener);
        window.removeEventListener('storage', onStorage);
    };
}
